// core/srs/learning.js — the card learning-state machine (L·2). Alivia's design, confirmed by the
// end user: free-recall-first on unlearned cards made her quit, so a card must be EARNED into
// flashcard recall by proving RECOGNITION first — real successive relearning / mastery-gating.
//
// The lifecycle (settled with the end user — do not relitigate):
//   learning  — a never-graduated card that HAS a linked MCQ. Presented as MCQ only (the quiz pool).
//               `streak` = consecutive correct answers in the pool.
//   probation — graduated (GRADUATE_AT correct in a row) and awaiting its FIRST cold recall (a
//               flashcard, no options). The demotion rule is ARMED here, and ONLY here.
//   review    — a normal FSRS card: either it passed its first cold recall, OR it never needed the
//               pool (no MCQ exists for it → recall is its only honest mode; or it already had FSRS
//               history when the machine first saw it — never retro-demote a card she's been studying).
//
// Transitions:
//   onQuizAnswer(state, correct)  — learning only. correct → streak+1 (graduate at GRADUATE_AT →
//                                   probation); wrong → streak 0 (stays learning, no penalty).
//   onRecallGrade(state, grade)   — probation + "again" (Blank) → DEMOTE to learning (streak 0);
//                                   probation + hard/good/easy → review (demotion DISARMED). A card
//                                   already in review is unchanged — a later Again reschedules via FSRS,
//                                   it does NOT demote (the disarm that stops a known card looping back
//                                   into the quiz pool).
//
// CRITICAL SEPARATION (data integrity, non-negotiable): the MCQ answer drives GRADUATION, never FSRS.
// These functions move learning-state ONLY; FSRS state is written ONLY by a flashcard grade
// (store.recordReview). The two write paths are physically distinct — proven in tests. PURE: no I/O,
// no writes, clock injected. Client-bundled via the `@school-apps/core/srs/learning` subpath (L·3).
import { RATINGS } from "./fsrs.js"; // the grade key-set (again/hard/good/easy) — one source of truth

export const PHASES = Object.freeze({ LEARNING: "learning", PROBATION: "probation", REVIEW: "review" });

/** Consecutive correct quiz answers that graduate a card out of the pool into recall (probation). */
export const GRADUATE_AT = 2;

/**
 * The starting learning-state for a card the machine has not seen before (no learn_ record yet). Pure —
 * the store supplies the two facts it can't know itself:
 *   existing FSRS history  → review   (never retro-demote a card she's already studying)
 *   else has a linked MCQ  → learning (must be earned into recall via recognition first)
 *   else (no MCQ)          → review   (recall is its only honest mode — nothing to graduate from)
 * @param {string} cardId
 * @param {{ hasMcq?: boolean, hasHistory?: boolean }} facts
 * @returns {{ cardId: string, phase: string, streak: number }}
 */
export function initLearningState(cardId, { hasMcq = false, hasHistory = false } = {}) {
  const phase = hasHistory ? PHASES.REVIEW : hasMcq ? PHASES.LEARNING : PHASES.REVIEW;
  return { cardId, phase, streak: 0 };
}

/**
 * Apply one QUIZ (MCQ) answer — GRADUATION logic only. NEVER touches FSRS. A non-learning card is not
 * in the quiz pool, so this is a no-op for it (returns the state unchanged).
 * @param {{ cardId: string, phase: string, streak: number, graduatedAt?: string }} state
 * @param {boolean} correct
 * @param {string} [at] ISO — stamped as graduatedAt when the card graduates
 * @returns the next learning-state
 */
export function onQuizAnswer(state, correct, at) {
  if (state.phase !== PHASES.LEARNING) return state; // not in the quiz pool — nothing to advance
  if (!correct) return { cardId: state.cardId, phase: PHASES.LEARNING, streak: 0 }; // reset, no penalty
  const streak = state.streak + 1;
  if (streak >= GRADUATE_AT) return { cardId: state.cardId, phase: PHASES.PROBATION, streak: 0, graduatedAt: at };
  return { cardId: state.cardId, phase: PHASES.LEARNING, streak };
}

/**
 * Apply one RECALL (flashcard) grade to the learning-state. Moves learning-state ONLY — the FSRS write
 * is a separate path (store.recordReview) this never calls.
 *   probation + "again" (Blank)     → DEMOTE to learning, streak 0 (the demotion is armed here only)
 *   probation + hard/good/easy      → review (now a normal FSRS card; demotion DISARMED)
 *   review (or learning) + anything → unchanged (a later Again reschedules via FSRS, never demotes)
 * @param {{ cardId: string, phase: string, streak: number, graduatedAt?: string }} state
 * @param {"again"|"hard"|"good"|"easy"} grade
 * @returns the next learning-state
 */
export function onRecallGrade(state, grade) {
  if (!(grade in RATINGS)) throw new Error(`unknown grade: ${grade}`);
  if (state.phase !== PHASES.PROBATION) return state; // only the FIRST post-graduation recall is armed
  if (grade === "again") return { cardId: state.cardId, phase: PHASES.LEARNING, streak: 0 }; // demote
  return { ...state, phase: PHASES.REVIEW }; // passed the cold recall — disarmed, keep graduatedAt
}
